/**
 * i18next, with the Polish tree as the schema.
 *
 * INLINE TEXT IS A BUG, even though today there is one real locale. The reason
 * is not future-proofing — it is that a phrase tree is the only place a
 * non-programmer can be handed the whole copy of the app, and every string that
 * escapes it is a string nobody can review.
 *
 * There is no HTTP backend and no language detector plugin: both locales are a
 * few kilobytes, they are bundled, and an installed PWA opening offline must
 * never be missing its own words.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { en } from './locales/en.ts';
import { pl } from './locales/pl.ts';

export const LOCALES = ['pl', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

const isLocale = (value: string): value is Locale => (LOCALES as readonly string[]).includes(value);

const preferred = (): Locale => {
  const stored = localStorage.getItem('puppy-doctor:locale');
  if (stored !== null && isLocale(stored)) return stored;
  const browser = navigator.language.slice(0, 2);
  return isLocale(browser) ? browser : 'pl';
};

void i18next.use(initReactI18next).init({
  resources: {
    pl: { translation: pl },
    en: { translation: en },
  },
  lng: preferred(),
  fallbackLng: 'pl',
  interpolation: {
    // React escapes everything already; doing it twice renders &amp; to users.
    escapeValue: false,
  },
});

/**
 * Whether the language was picked by hand since this tab opened.
 *
 * The user document is the cross-device source of truth — that is the point of
 * storing a locale on it — but it arrives from a listener, hundreds of
 * milliseconds after the app has painted. Without this flag, someone who
 * switched to English on the sign-in screen and then signed in would watch the
 * app flip back to Polish under their thumb, which reads as the app ignoring
 * them rather than as two sources of truth reconciling.
 */
let chosen = false;

const apply = (locale: Locale): void => {
  localStorage.setItem('puppy-doctor:locale', locale);
  void i18next.changeLanguage(locale);
};

/**
 * Set because the person chose, here, now — from the picker on the sign-in
 * screen or in their settings. Always wins over the profile for the rest of this
 * session; see `adoptLocale`.
 */
export const setLocale = (locale: Locale): void => {
  chosen = true;
  apply(locale);
};

/**
 * Take the locale from the signed-in person's profile, so a language chosen on
 * their phone is the language their tablet opens in.
 *
 * Deliberately tolerant of anything: it is fed straight from a Firestore
 * document, which may have been written by an older build, by hand in the
 * console, or not at all.
 */
export const adoptLocale = (value: unknown): void => {
  if (chosen) return;
  if (typeof value !== 'string' || !isLocale(value)) return;
  if (i18next.language === value) return;
  apply(value);
};

/** The active locale, narrowed — `i18next.language` is a plain string. */
export const currentLocale = (): Locale =>
  LOCALES.find((locale) => i18next.language.startsWith(locale)) ?? 'pl';

/** The shape every locale must satisfy. See en.ts. */
export type Phrases = typeof pl;

declare module 'i18next' {
  interface CustomTypeOptions {
    resources: { translation: Phrases };
  }
}

export default i18next;
